// src/pages/PublicWorkshopProfile.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import API_URL from '../api/baseURL';

const StarRow = ({ value, size = 20 }) => {
  if (!value || value === 'N/A') return null;
  const rounded = Math.round(Number(value));
  return (
    <span className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 20 20" fill={i < rounded ? "#facc15" : "#e5e7eb"}>
          <polygon points="10,1 12,7 18,7 13,11 15,17 10,13 5,17 7,11 2,7 8,7" />
        </svg>
      ))}
      <span className="ml-2 font-bold text-yellow-400">{value}</span>
    </span>
  );
};

// ---- helpers (added) ----
const toArray = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(x => String(x).trim()).filter(Boolean);
  return String(v).split(/[,;|]/).map(s => s.trim()).filter(Boolean);
};
const pick = (...vals) => vals.find(v => v !== undefined && v !== null && String(v).trim() !== '') || '';
const onlyDigits = (s) => String(s || '').replace(/\D+/g, '');
const waLink = ({ phone, text }) => {
  const digits = onlyDigits(phone);
  if (!digits) return '';
  return `https://wa.me/${digits}?text=${encodeURIComponent(text || '')}`;
};

const PublicWorkshopProfile = () => {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetch(`${API_URL}/workshops/${id}/public`)
      .then(res => res.json())
      .then(setProfile)
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-8">Loading...</div>;
  if (!profile) return <div className="p-8 text-red-500">Workshop not found.</div>;

  // ---- normalized fields (added) ----
  const businessName = pick(profile.businessName, profile.name);
  const logo = pick(profile.logo, profile.avatar, profile.image);
  const address = pick(profile.address, profile.details?.address, profile.details?.location);
  const phone = pick(profile.phone, profile.contactNumber, profile.details?.phone);
  const email = pick(profile.email, profile.details?.email);
  const website = pick(profile.website, profile.details?.website, profile.site);
  const avg = pick(profile.averageRating, profile.avgRating, profile.rating, 'N/A');
  const reviewCount = Number(profile.reviewCount || profile.reviewsCount || (profile.reviews?.length ?? 0)) || 0;

  const specialties = toArray(
    profile.specializations ||
    profile.specialties ||
    profile.expertise ||
    profile.servicesOffered ||
    profile.categories
  );

  const services = toArray(profile.services || profile.serviceList);
  const gallery = Array.isArray(profile.images || profile.gallery) ? (profile.images || profile.gallery) : [];

  const hours =
    profile.openingHours ||
    profile.hours ||
    profile.schedule ||
    null; // can be object {Mon:".."} or array

  const mapHref = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : '';
  const waText = `Bonjour, je souhaite un devis pour mon véhicule via 1PARC. Atelier: ${businessName}`;
  const waUrl = waLink({ phone, text: waText });

  return (
    <div className="max-w-3xl mx-auto p-8 bg-white dark:bg-gray-900 rounded-lg shadow">
      <div className="flex items-center gap-6 mb-4">
        {logo && (
          <img src={logo} alt="logo" className="w-16 h-16 rounded-full bg-gray-200 object-cover" />
        )}
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-1">{businessName}</h1>
          <StarRow value={avg} />
          <p className="text-sm text-gray-500">{reviewCount} review(s)</p>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><span className="font-semibold">Address:</span> {address || '—'} {mapHref && (
          <a href={mapHref} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-500 underline text-sm">Map</a>
        )}</div>
        <div><span className="font-semibold">Phone:</span> {phone || '—'}</div>
        <div><span className="font-semibold">Email:</span> {email || '—'}</div>
        <div><span className="font-semibold">Website:</span> {website
          ? <a className="text-blue-500 underline" href={website} target="_blank" rel="noopener noreferrer">{website}</a>
          : '—'}</div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        {phone && waUrl && (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white font-semibold"
          >
            Contacter sur WhatsApp
          </a>
        )}
        {phone && (
          <a
            href={`tel:${onlyDigits(phone)}`}
            className="px-4 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Appeler
          </a>
        )}
        {email && (
          <a
            href={`mailto:${email}?subject=Demande de devis via 1PARC&body=${encodeURIComponent('Bonjour, je souhaite un devis pour mon véhicule via 1PARC.')}`}
            className="px-4 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Email
          </a>
        )}
      </div>

      {/* Specializations */}
      {specialties.length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2 text-blue-500">Spécialisations</h2>
          <div className="flex flex-wrap gap-2">
            {specialties.map((tag, i) => (
              <span key={i} className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-900 text-sm">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Services */}
      {services.length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2 text-emerald-500">Services</h2>
          <ul className="list-disc pl-5 space-y-1 text-gray-700 dark:text-gray-300">
            {services.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}

      {/* Opening hours */}
      {hours && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2 text-purple-500">Horaires d’ouverture</h2>
          <table className="w-full text-sm">
            <tbody className="text-gray-700 dark:text-gray-300">
              {Array.isArray(hours)
                ? hours.map((row, i) => (
                    <tr key={i} className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-1 font-medium">{row.day || row.label || `Jour ${i+1}`}</td>
                      <td className="py-1">{row.hours || row.time || row.value || '—'}</td>
                    </tr>
                  ))
                : Object.entries(hours).map(([day, val]) => (
                    <tr key={day} className="border-b border-gray-200 dark:border-gray-700">
                      <td className="py-1 font-medium capitalize">{day}</td>
                      <td className="py-1">{String(val)}</td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2 text-pink-500">Galerie</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {gallery.slice(0, 6).map((src, i) => (
              <img key={i} src={src} alt={`photo-${i}`} className="w-full h-32 object-cover rounded border border-gray-200 dark:border-gray-800" />
            ))}
          </div>
        </div>
      )}

      {/* Existing Reviews (kept your block) */}
      <div>
        <h2 className="text-xl font-semibold mb-2 text-blue-400">Latest Reviews</h2>
        {profile.reviews && profile.reviews.length > 0 ? (
          <ul className="space-y-2">
            {profile.reviews.map((r, i) => (
              <li key={i} className="border-b pb-2 border-gray-200 dark:border-gray-800">
                <StarRow value={r.rating} size={16} />
                {r.comment && <div className="text-gray-700 dark:text-gray-300 mt-1">{r.comment}</div>}
                <div className="text-xs text-gray-400">{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : ''}</div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-gray-400">No reviews yet.</div>
        )}
      </div>
    </div>
  );
};

export default PublicWorkshopProfile;
